import { PhoneInput } from 'react-international-phone';
import 'react-international-phone/style.css';
import { StateCombobox } from '@/components/shared';
import { montarEnderecoCompleto } from '../../../models/candidato.opcoes';
import { formatarCEP } from '../../../utils/masks';
import {
  INPUT_CLASS,
  LABEL_CLASS,
  RespostasEtapa2Estado,
  telefoneValido,
} from '../candidatoFormExternoEtapa2Page.definitions';

interface EtapaContatoEnderecoProps {
  respostas: RespostasEtapa2Estado;
  onChange: <K extends keyof RespostasEtapa2Estado>(campo: K, valor: RespostasEtapa2Estado[K]) => void;
}

export default function EtapaContatoEndereco({ respostas, onChange }: EtapaContatoEnderecoProps) {
  // Endereço em partes (nada fica faltando, ex.: o número) e montado no padrão único que o
  // RH usa no perfil — ver montarEnderecoCompleto. O complemento vai no campo próprio.
  const enderecoMontado = montarEnderecoCompleto(respostas);

  return (
    <>
      <h2 className="mb-6 text-2xl font-bold text-gray-800">Contato de Emergência e Endereço</h2>
      <div className="space-y-5">
        <div>
          <label className={LABEL_CLASS}>Nome do contato de emergência *</label>
          <input
            type="text"
            className={INPUT_CLASS}
            value={respostas.contatoEmergenciaNome ?? ''}
            onChange={(e) => onChange('contatoEmergenciaNome', e.target.value)}
          />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className={LABEL_CLASS}>Telefone *</label>
            <PhoneInput
              value={respostas.contatoEmergenciaTelefone ?? ''}
              onChange={(valor) => onChange('contatoEmergenciaTelefone', valor)}
              defaultCountry="br"
              inputClassName={INPUT_CLASS}
            />
            {respostas.contatoEmergenciaTelefone && !telefoneValido(respostas.contatoEmergenciaTelefone) && (
              <p className="mt-1 text-sm text-red-600">Informe o telefone completo, com DDD e número.</p>
            )}
          </div>
          <div>
            <label className={LABEL_CLASS}>Grau de parentesco *</label>
            <input
              type="text"
              className={INPUT_CLASS}
              value={respostas.contatoEmergenciaParentesco ?? ''}
              onChange={(e) => onChange('contatoEmergenciaParentesco', e.target.value)}
            />
          </div>
        </div>

        <h3 className="pt-2 text-lg font-semibold text-gray-800">Endereço</h3>
        <div className="grid grid-cols-3 gap-4">
          <div>
            <label className={LABEL_CLASS}>CEP *</label>
            <input
              type="text"
              inputMode="numeric"
              placeholder="00000-000"
              className={INPUT_CLASS}
              value={formatarCEP(respostas.enderecoCep ?? '')}
              onChange={(e) => onChange('enderecoCep', formatarCEP(e.target.value))}
            />
          </div>
          <div className="col-span-2">
            <label className={LABEL_CLASS}>Rua / Avenida *</label>
            <input
              type="text"
              className={INPUT_CLASS}
              value={respostas.enderecoLogradouro ?? ''}
              onChange={(e) => onChange('enderecoLogradouro', e.target.value)}
            />
          </div>
        </div>
        <div className="grid grid-cols-3 gap-4">
          <div>
            <label className={LABEL_CLASS}>Número *</label>
            <input
              type="text"
              className={INPUT_CLASS}
              value={respostas.enderecoNumero ?? ''}
              onChange={(e) => onChange('enderecoNumero', e.target.value)}
            />
          </div>
          <div className="col-span-2">
            <label className={LABEL_CLASS}>Complemento</label>
            <input
              type="text"
              placeholder="Apto, bloco, casa..."
              className={INPUT_CLASS}
              value={respostas.enderecoComplemento ?? ''}
              onChange={(e) => onChange('enderecoComplemento', e.target.value)}
            />
          </div>
        </div>
        <div>
          <label className={LABEL_CLASS}>Bairro *</label>
          <input
            type="text"
            className={INPUT_CLASS}
            value={respostas.enderecoBairro ?? ''}
            onChange={(e) => onChange('enderecoBairro', e.target.value)}
          />
        </div>
        <div className="grid grid-cols-3 gap-4">
          <div className="col-span-2">
            <label className={LABEL_CLASS}>Cidade *</label>
            <input
              type="text"
              className={INPUT_CLASS}
              value={respostas.enderecoCidade ?? ''}
              onChange={(e) => onChange('enderecoCidade', e.target.value)}
            />
          </div>
          <div>
            <label className={LABEL_CLASS}>Estado (UF) *</label>
            <StateCombobox
              selectedValue={respostas.enderecoUf ?? null}
              onChange={(valor) => onChange('enderecoUf', valor ?? '')}
            />
          </div>
        </div>
        <div>
          <label className={LABEL_CLASS}>País *</label>
          <input
            type="text"
            className={INPUT_CLASS}
            value={respostas.enderecoPais ?? ''}
            onChange={(e) => onChange('enderecoPais', e.target.value)}
          />
        </div>
        {enderecoMontado && (
          <p className="rounded-xl bg-gray-50 p-3 text-sm text-gray-600">
            <span className="font-semibold">Seu endereço ficará assim:</span> {enderecoMontado}
            {respostas.enderecoComplemento?.trim() ? ` (${respostas.enderecoComplemento.trim()})` : ''}
          </p>
        )}
      </div>
    </>
  );
}
